<template>
    <div>
        <loading-modal v-if="isLoading" loading-text="Loading Report Data..." />

        <div class="report-toolbar">
				<v-layout row>
					<v-flex>
						<v-layout row wrap>
								<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">	
									<v-text-field v-debounce:300ms="updateSearch" ref="focusAfterTagSelector" append-icon="search" label="Search Employees" autofocus="" background-color="#fff"></v-text-field>
								</v-flex>
								<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
									<tag-selector :options="tagList"
												:selectedOptions="filters.selectedTags"
												:isMatchAll="filters.matchAllTags"
												placeholder="Enter Tag"
												@on-selected-tags-change="updateSelectedTags"
												@on-match-all-tags-change="updateMatchAllTags"
												@on-leave="onLeaveTagSelector"></tag-selector>
								</v-flex>
                                <v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
                                    <v-select v-model="filters.department" :items="departmentsOrdered" item-text="name" item-value="id" label="Department"></v-select>
                                </v-flex>
                                <v-flex shrink pl-2>
                                    <v-switch v-model="filters.activeEmployeesOnly" label="Active Employees Only"></v-switch>
                                </v-flex>
						</v-layout>
					</v-flex>

					<v-flex shrink>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn icon v-on="on" @click="print">
									<v-icon>print</v-icon>
								</v-btn>
							</template>
							<span>Print Report</span>
						</v-tooltip>
					</v-flex>
				</v-layout>				
			</div>

        <v-container v-if="reportData" grid-list-xl pa-0 class="mt-3" fluid>
            <v-layout row wrap>
						<v-flex xl5 l12 md12 sm12>
                            <highcharts v-if="showChart" :options="chartOptions" :callback="renderChartQuadrants" ref="chart"></highcharts>
						</v-flex>
                        <v-flex xl7 lg12 md12 sm12>
                           <v-card class="print-no-borders">
                               <v-toolbar flat dense>
                                    <v-toolbar-title class="subheading">Employees</v-toolbar-title>
                                    <v-spacer></v-spacer>
                                    <!-- <v-switch v-model="filters.activeEmployeesOnly" label="Active Employees Only"></v-switch> -->
                                </v-toolbar>
                             <v-data-table :headers="headers" class="elevation-1" :items="employeesList" :pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems" disable-initial-sort>
                                        <template v-slot:items="props">
                                            <tr @mouseover="onEmployeeListRowHover(props.item.id)" :class="{ 'inactive': !props.item.isActive, 'employee-row-hover' : props.item.id === hoveredEmployeeId }">
                                                <td class="text-xs-left">
                                                    <v-avatar size="30px">
                                                        <img v-if="props.item !== null" :src="getAvatar(props.item.id)" class="avatar-img" />
                                                    </v-avatar>
                                                    <router-link class="ml-3" :to="{ name: 'employee', params: { id: props.item.id } }">{{ props.item.fullName }}</router-link>
                                                </td>
                                                <td class="text-xs-left">
                                                    <span v-if="props.item.hasReview">{{props.item.performance}}</span>
                                                   	<span v-else class="no-score-text">N/A</span>
                                                </td>
                                                <td class="text-xs-left">
                                                    <span v-if="props.item.hasReview">{{props.item.values}}</span>
                                                    <span v-else class="no-score-text">N/A</span>
                                                </td>
                                                 <td class="text-xs-left">
                                                     <router-link v-if="props.item.hasReview" :to="{ name: 'pr', params: { id: props.item.lastReviewId } }">
                                                        {{ props.item.lastReviewDateUtc | formatDate }}
                                                    </router-link>
                                                    <v-tooltip v-if="props.item.isActive && isOlderThan1Year(props.item.lastReviewDateUtc)" bottom>
                                                        <v-icon slot="activator" color="warning" small class="ml-1">mdi-alert-circle-outline</v-icon>                                                                                       
                                                        <span>This review was completed over 1 year ago. Consider setting up a new review.</span>
                                                    </v-tooltip>
                                                </td>
                                                 <td class="text-xs-left">
                                                    <span v-if="props.item.hasReview">{{props.item.lastReviewedBy}}</span>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                            </v-card>
                        </v-flex>
            </v-layout>
        </v-container>
    </div>
</template>

<script>
import {Chart} from 'highcharts-vue'
import avatarMixin from '@/mixins/avatarMixin';
import LoadingModal from '@/components/LoadingModal';
import moment from 'moment';
import { mapGetters } from 'vuex';
import TagSelector from '@/components/tags/TagSelector';

var resizeId;

export default {
    data() {
        return {
            headers: [
                { text: 'Employee', value: 'fullName' },
                { text: 'Performance Score', value: 'performance' },
                { text: 'Values Score', value: 'values' },
                { text: 'Last Review Date', value: 'lastReviewDate' },
                { text: 'Last Reviewed By', value: 'lastReviewer' },
            ],
            reportData: null,
            isLoading: false,
            hoveredEmployeeId: null,
            filters: {
                activeEmployeesOnly: true,
                search: null,
                department: -1,
                selectedTags: [],
				matchAllTags: false
            },
            showChart: true,
            pagination: {
					sortBy: 'name',
					rowsPerPage: 10
            },
            rowsPerPageItems: [10, 20, 30, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }],
        }
    },
    components: {
        highcharts: Chart,
        LoadingModal,
        TagSelector
    },
    async created() {
        this.isLoading = true;
        await this.getReportData();
        this.isLoading = false;

        window.addEventListener('resize', () => {
            clearTimeout(resizeId);
            
            resizeId = setTimeout(() => {
                if (this.$refs.chart) {
                    this.showChart = false;
                    this.$nextTick(() => {this.showChart = true;})
                }
            }, 150)     
        });

    },
    mixins: [avatarMixin],
    computed: {
        ...mapGetters({
            loggedInEmployee: 'loggedInEmployee',
			departments: 'departments/departments',
            tagList: 'tags/employeeTagList'
		}),
        chartOptions() {
            var _this = this;
            return {
                chart: {
                    type: 'scatter',
                    zoomType: ''
                },
                legend: {
                    enabled: false
                },
                title: {
                    text: 'Performance vs Values Report'
                },
                subtitle: {
                    text: 'Data provided by Reviewers'
                },
                xAxis: {
                    title: {
                        enabled: true,
                        text: 'Performance'
                    },
                    startOnTick: true,
                    endOnTick: true,
                    showLastLabel: true,
                    labels: {
                        enabled: true,
                        formatter: function() {
                                if (this.isLast || this.isFirst) {
                                return this.value
                            }
                        }
                    },
                    min:0,
                    max:100,
                    plotLines:[{
                        value:50,
                        dashStyle: 'solid',
                        color:'#666',
                        width:1,
                        zIndex: 5
                    }]
                },
                yAxis: {
                    title: {
                        text: 'Values Match'
                    },
                     labels: {
                        enabled: true,
                        formatter: function() {
                                if (this.isLast || this.isFirst) {
                                return this.value
                            }
                        }
                    },
                    min:0,
                    max:100,
                    plotLines:[{
                        value:50,
                        dashStyle: 'solid',
                        color:'#666',
                        width:1,
                        zIndex: 5
                    }],
                },
                plotOptions: {
                    scatter: {
                        jitter: {
                            x: 0.24,
                            y: 0
                        },
                        marker: {
                            radius: 7,
                            symbol: 'circle',
                            states: {
                                hover: {
                                    enabled: true,
                                    lineColor: 'rgb(100,100,100)'
                                }
                            }
                        },
                        states: {
                            hover: {
                                marker: {
                                    enabled: false
                                }
                            }
                        },
                        tooltip: {
                            headerFormat: '<b>{point.key}</b><br>',
                            pointFormat: 'Performance Score: {point.x}%<br>Values Score: {point.y}%'
                        },
                    },
                    series: {
                            point: {
                                events: {
                                    mouseOver: function () {
                                        _this.onEmployeePointHover(this.employeeId);
                                    }
                                }
                            },
                            events: {
                                mouseOut: function () {
                                    _this.onEmployeePointHover(null);
                                }
                            }
                        }
                },
                series: [
                    {
                        name: 'Employee Data',
                        data: this.employeeReportData
                    },
                ]     
            }      
        },
        filteredEmployeeIds() {
            const filterOptions = {
                search: this.filters.search,
                tags: this.filters.selectedTags,
                matchAllTags: this.filters.matchAllTags,
                departmentId: this.filters.filterDepartment !== -1 ? this.filters.filterDepartment : null
            };
            return this.$_getFilteredEmployeeIds(this.loggedInEmployee, filterOptions);
        },
        filteredEmployees() {
            let filtered = this.reportData.employees;

            // Default filtering
			filtered = filtered.filter(x => this.filteredEmployeeIds.includes(x.id));
            
            // Active only
            if (this.filters.activeEmployeesOnly) {
                filtered = filtered.filter(x => x.isActive);
            }
            
            return filtered;
        },
        employeeReportData() {
            return this.filteredEmployees
            .filter(x => x.hasReview)
            .map(employee => {
                        return {
                            x: employee.reviewerPerformanceScore,
                            y: employee.reviewerValuesScore,
                            name: employee.fullName,
                            color: "rgba(33, 150, 243, .5)",
                            employeeId: employee.id
                        }
                    })
        },
        employeesList() {
            return this.filteredEmployees.map(x => {
                return {
                    id: x.id,
                    fullName: x.fullName,
                    values: x.reviewerValuesScore,
                    performance: x.reviewerPerformanceScore,
                    totalScore: x.hasReview ? x.reviewerValuesScore * x.reviewerPerformanceScore : 0,
                    hasReview: x.hasReview,
                    lastReviewedBy: x.lastReviewedBy,
                    lastReviewDateUtc: x.lastReviewDateUtc,
                    lastReviewId: x.lastReviewId,
                    isActive: x.isActive
                }
            }).sort((a, b) => {
                if (b.totalScore > a.totalScore) {
                    return 1;
                } else if (b.totalScore < a.totalScore) {
                    return - 1;
                }
                // Secondary sort
                return a.fullName.localeCompare(b.fullName);
            })
        },
        departmentsOrdered() {
            if (!this.reportData) return [];

            var departments = this.departments.slice(0);

            const availableDepartmentIds = this.reportData.employees
                .filter(x => x.department != null)
                .map(x => x.department.id);

            departments = departments.filter(x => availableDepartmentIds.includes(x.id));

            var sorted = departments.sort();
            sorted.unshift({ id: -1, name: 'All Departments' });
            return sorted;
		},
    },
    methods: {
        renderChartQuadrants(chart) {
            var width = chart.plotBox.width / 2.0;
            var height = chart.plotBox.height / 2.0 + 1;
    
            // // Top left
            // chart.renderer.rect(chart.plotBox.x,chart.plotBox.y, width, height, 1)
            // .attr({
            //     fill: 'rgba(251, 140, 0, 0.1)',
            //     zIndex: 0
            // })
            // .add();

            // Top right
            chart.renderer.rect(chart.plotBox.x + width, chart.plotBox.y, width, height, 1)
            .attr({
                fill: 'rgba(0, 255, 0, 0.1)',
                zIndex: 0
            })
            .add();

            // // Bottom Left
            // chart.renderer.rect(chart.plotBox.x, chart.plotBox.y + height, width, height, 1)
            // .attr({
            //     fill: 'rgba(255, 0, 0, 0.1)',
            //     zIndex: 0
            // })
            // .add();

            // // Bottom right
            // chart.renderer.rect(chart.plotBox.x + width, chart.plotBox.y + height, width, height, 1)
            // .attr({
            //     fill: 'rgba(251, 140, 0, 0.1)',
            //     zIndex: 0
            // })
            // .add();
           
        },
        async getReportData() {
            try {
                const response = await this.$store.dispatch('reports/get', 'PerformanceValuesReport');
                this.reportData = response.data;
            } catch (e) {
                
            }
        },
        onEmployeeListRowHover(employeeId) {
            const index = this.$refs.chart.chart.series[0].points.findIndex(x => x.employeeId === employeeId);
            if(index > -1) {
                this.$refs.chart.chart.series[0].data[index].setState('hover');
                this.$refs.chart.chart.tooltip.refresh(this.$refs.chart.chart.series[0].data[index]);
            } else {
                this.$refs.chart.chart.tooltip.destroy();
            }
        },
        onEmployeePointHover(employeeId) {
            this.hoveredEmployeeId = employeeId;
        },
        isOlderThan1Year(date) {
            return moment(date).isBefore(moment().subtract(1, 'years'));
        },
        updateSearch(val) {
			this.filters.search = val;
		},
        updateSelectedTags(newSelectedTags) {
            this.filters.selectedTags = newSelectedTags;
        },
        updateMatchAllTags(newMatchAll) {
            this.filters.matchAllTags = newMatchAll;
        },
        onLeaveTagSelector() {
            this.$refs.focusAfterTagSelector.focus();
        },
        print() {
            window.print();
        },
    }
}
</script>

<style scoped>
 .no-score-text {
     color: rgba(0,0,0,.54);
     font-style: italic;
 }
 .employee-row-hover {
     background: #eee;
 }
 .inactive {
	background-color: #ffebee;
}
</style>